import React, { useState } from 'react';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import {
  readOrphanedQueue,
  clearOrphanedQueue,
  formatQueuedAt,
  type OrphanedEntry,
} from '../../lib/rentals-offline-recovery';

/**
 * Surfaces work the old localStorage queue swallowed.
 *
 * Renders nothing in the normal case — the queue is empty on any device that
 * never hit the old offline path, and empty forever on any device that has
 * logged in or out since (purgeTenantData clears the key on BOTH, which is why
 * this had to ship without waiting for a device audit).
 *
 * The dismiss is what removes the key, and it is the only thing that does. On
 * mount this only READS: a banner that cleared what it was reporting would lose
 * the work a second time.
 */
export default function OrphanedOfflineWork() {
  // Read once, at first render. Re-reading on every render would fight the
  // dismiss, and nothing else in the app writes this key any more.
  const [entries, setEntries] = useState<OrphanedEntry[]>(() => readOrphanedQueue());
  const [copied, setCopied] = useState(false);

  if (entries.length === 0) return null;

  const handleCopy = async () => {
    const text = entries
      .map((e) => `${e.label} — order ${e.orderId ?? 'unknown'} — ${formatQueuedAt(e.timestamp)}\n${JSON.stringify(e.raw, null, 2)}`)
      .join('\n\n---\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure origin, permissions). The details are still
      // on screen to read off manually, which is the fallback that matters.
      setCopied(false);
    }
  };

  const handleDismiss = () => {
    clearOrphanedQueue();
    setEntries([]);
  };

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/40 px-4 py-3">
      <div className="max-w-[430px] mx-auto">
        <div className="flex items-start gap-2 mb-2">
          <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">
              {entries.length === 1
                ? '1 item was never sent'
                : `${entries.length} items were never sent`}
            </p>
            <p className="text-xs text-amber-200/80 mt-0.5">
              An older version of this app saved these on the device instead of
              sending them. They did not reach the office. Redo the work below,
              then dismiss this.
            </p>
          </div>
        </div>

        <ul className="space-y-1.5 mb-3">
          {entries.map((e, i) => (
            /**
             * Keyed on the INDEX, deliberately. The obvious key —
             * type + timestamp — collides for two entries queued in the same
             * second, and React's response to a duplicate key is to omit or
             * duplicate a child. A banner whose entire job is to report lost
             * work must not under-report it. The list is read once into state
             * and never reordered, so the index is stable here.
             */
            // eslint-disable-next-line react/no-array-index-key
            <li
              key={i}
              className="text-xs text-amber-100 bg-amber-500/10 rounded-lg px-3 py-2"
            >
              <span className="font-semibold">{e.label}</span>
              {e.orderId && <span> · order {e.orderId}</span>}
              <span className="block text-amber-200/70">
                recorded {formatQueuedAt(e.timestamp)}
              </span>
            </li>
          ))}
        </ul>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-500/50 text-amber-300 text-xs font-semibold min-h-[40px]"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy details'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-200 text-xs font-semibold min-h-[40px]"
          >
            I've dealt with this
          </button>
        </div>
      </div>
    </div>
  );
}
